import { useState, useRef, useEffect } from 'react';
import './SearchableSelect.css';

interface Option {
  value: number;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** id du champ de saisie, pour un <label htmlFor> */
  inputId?: string;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Rechercher...',
  required = false,
  disabled = false,
  inputId,
}: SearchableSelectProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Sync display text with selected value
  const selectedLabel = options.find((o) => o.value === value)?.label || '';

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        // Reset search to selected label when closing
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  const handleSelect = (opt: Option) => {
    onChange(opt.value);
    setSearch('');
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleInputChange = (text: string) => {
    setSearch(text);
    if (!isOpen) setIsOpen(true);
    setHighlightIndex(-1);
    // Clear selection if user is typing something different
    if (value !== 0 && text !== selectedLabel) {
      onChange(0);
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
    setSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
      inputRef.current?.blur();
    }
  };

  const displayValue = isOpen ? search : selectedLabel;

  return (
    <div className="searchable-select" ref={containerRef}>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        className="searchable-select-input"
        value={displayValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required && value === 0}
        disabled={disabled}
        autoComplete="off"
      />
      {/* Hidden input for form validation */}
      {required && <input type="hidden" value={value || ''} required />}

      {isOpen && (
        <ul className="searchable-select-dropdown" ref={listRef}>
          {filtered.length === 0 ? (
            <li className="searchable-select-empty">Aucun résultat</li>
          ) : (
            filtered.map((opt, idx) => (
              <li
                key={opt.value}
                className={`searchable-select-option${opt.value === value ? ' selected' : ''}${idx === highlightIndex ? ' highlighted' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
